import { z } from 'zod';

/* ---------- Shared ---------- */

const NullableDate = z.string().nullable().catch(null);

const DimensionSchema = z.object({
  unit: z.string().catch(''),
  value: z.number().catch(0),
});

const AddressSchema = z.object({
  address_line_one: z.string().catch(''),
  address_line_two: z.string().catch(''),
  city: z.string().catch(''),
  state: z.string().catch(''),
  zip_code: z.string().catch(''),
  country: z.string().catch(''),
});

/* ---------- WMS Metadata ---------- */

const WmsMetadataSchema = z.object({
  wmsOrganizationId: z.string().catch(''),
  wmsWarehouseId: z.string().catch(''),
});

/* ---------- Origin / Destination ---------- */

const LocationSchema = z.object({
  _id: z.string().catch(''),
  name: z.string().catch(''),
  type: z.string().optional().catch(''),
  integration_type: z.string().optional().catch(''),
  marketplace_region_id: z.string().optional().catch(''),
  address: AddressSchema.catch({}),
  wmsMetadata: WmsMetadataSchema.catch({
    wmsOrganizationId: '',
    wmsWarehouseId: '',
  }),
});

/* ---------- Alias ---------- */

const AliasAttributesSchema = z.object({
  carton_length: DimensionSchema.catch({ unit: '', value: 0 }),
  carton_width: DimensionSchema.catch({ unit: '', value: 0 }),
  carton_height: DimensionSchema.catch({ unit: '', value: 0 }),
  carton_net_weight: DimensionSchema.catch({ unit: '', value: 0 }),
  carton_gross_weight: DimensionSchema.catch({ unit: '', value: 0 }),
  units_per_carton: z.number().catch(0),
});

const LocationCapabilitySchema = z.object({
  allowsMixedProducts: z.boolean().catch(false),
  allowsMixedLots: z.boolean().catch(false),
  isPickingLocation: z.boolean().catch(false),
  stackable: z.boolean().catch(false),
});

const LocationCapacitySchema = z.object({
  currentWeight: z.number().catch(0),
  currentUnits: z.number().catch(0),
  occupiedSpaces: z.number().catch(0),
  occupiedVolume: z.number().catch(0),
  trackWeight: z.boolean().catch(false),
  trackUnits: z.boolean().catch(false),
  trackDimensions: z.boolean().catch(false),
  totalSpaces: z.number().catch(0),
});

const AliasLocationSchema = z.object({
  _id: z.string().catch(''),
  warehouseId: z.string().catch(''),
  code: z.string().catch(''),
  capabilities: LocationCapabilitySchema.catch({}),
  capacity: LocationCapacitySchema.catch({}),
  zoneColor: z.string().catch(''),
});

const AliasMetadataSchema = z.object({
  checked_quantity: z.number().catch(0),
});

const AliasSchema = z.object({
  _id: z.string().catch(''),
  alias: z.string().catch(''),
  qty: z.number().catch(0),
  received_qty: z.number().catch(0),
  counted: z.number().catch(0),
  status: z.string().catch(''),
  expiration_date: NullableDate,

  is_inventory_deducted: z.boolean().catch(false),
  is_initial_amazon_connected_warehouse_qty_deducted: z.boolean().catch(false),
  is_deleted_shipment_inventory_deducted: z.boolean().catch(false),

  isVirtual: z.boolean().catch(false),
  itemLevel: z.boolean().catch(false),
  metadata: AliasMetadataSchema.catch(null),

  attributes: AliasAttributesSchema.catch({}),
  location: AliasLocationSchema.catch(null),
  receivedLocation: AliasLocationSchema.catch(null),
});

/* ---------- Items ---------- */

const ItemImagesSchema = z.object({
  display_image: z.string().catch(''),
});

const ShipmentItemSchema = z.object({
  itemId: z.string().catch(''),
  sku: z.string().catch(''),
  title: z.string().catch(''),
  upc: z.string().catch(''),
  receivedQty: z.number().catch(0),
  images: ItemImagesSchema.catch({ display_image: '' }),
  aliases: z.array(AliasSchema).catch([]),
});

/* ---------- Progress ---------- */

const ProgressSchema = z.object({
  percentage: z.number().catch(0),
  totalExpected: z.number().catch(0),
  totalCounted: z.number().catch(0),
  totalReceived: z.number().catch(0),
});

/* ---------- Organization ---------- */

const OrganizationSchema = z.object({
  _id: z.string().catch(''),
  company_name: z.string().catch(''),
  logo: z
    .object({
      key: z.string().catch(''),
      bucket_name: z.string().catch(''),
      url: z.string().catch(''),
    })
    .catch({ key: '', bucket_name: '', url: '' }),
});

/* ---------- Workflow ---------- */

const WorkflowSchema = z.object({
  arrivedAt: NullableDate,
  completedAt: NullableDate,
});

const DocumentSchema = z.object({
  _id: z.string().catch(''),
  exportId: z.string().catch(''),
  status: z.string().catch(''),
  documentType: z.string().catch(''),
  workflowType: z.string().catch(''),
  fileName: z.string().catch(''),
  fileType: z.string().catch(''),
  fileSize: z.number().catch(0),
  key: z.string().catch(''),
  bucketName: z.string().catch(''),
  filePath: z.string().catch(''),
  createdBy: z.string().catch(''),
  createdAt: z.string().catch(''),
  completedAt: z.string().catch(''),
});

/* ---------- Attention Emails ---------- */

const AttentionEmailSchema = z.object({
  _id: z.string().catch(''),
  email: z.string().catch(''),
  addedBy: z.string().catch(''),
  addedAt: z.string().catch(''),
});

/* ---------- Comments ---------- */

const CommentSchema = z.object({
  _id: z.string().catch(''),
  author: z.string().catch(''),
  authorName: z.string().catch(''),
  authorEmail: z.string().catch(''),
  text: z.string().catch(''),
  createdAt: z.string().catch(''),
});

const PalletItemSchema = z.object({
  alias: z.string().catch(''),
  upc: z.string().catch(''),
});

/* ---------- Root ---------- */
const PalletSchema = z.object({
  _id: z.string().catch(''),
  palletNumber: z.string().catch(''),
  embeddedIn: z.string().nullable().catch(null),
  contents: z.array(PalletItemSchema).catch([]),
});

export const WmsShipmentDetailSchema = z.object({
  _id: z.string().catch(''),
  displayId: z.string().catch(''),
  status: z.string().catch(''),
  wmsStatus: z.string().catch(''),

  qtyType: z.string().catch(''),
  workflowType: z.string().catch(''),
  shippingType: z.string().catch(''),

  shipped: NullableDate,
  arrived: NullableDate,
  completed: NullableDate,

  ETD: z.string().catch(''),
  ETA: z.string().catch(''),

  origin: LocationSchema.catch({}),
  destination: LocationSchema.catch({}),

  customer: z.any().catch({}),

  items: z.array(ShipmentItemSchema).catch([]),
  components: z.array(z.any()).catch([]),

  progress: ProgressSchema.catch({
    percentage: 0,
    totalExpected: 0,
    totalCounted: 0,
    totalReceived: 0,
  }),

  comments: z.array(CommentSchema).catch([]),
  documents: z.array(DocumentSchema).catch([]),
  attentionEmails: z.array(AttentionEmailSchema).catch([]),
  pallets: z.array(PalletSchema).catch([]),
  organization: OrganizationSchema.catch({}),
  workflow: WorkflowSchema.catch({ arrivedAt: null, completedAt: null }),
});

/* ---------- Type ---------- */

export type WmsShipmentInboundType = z.infer<typeof WmsShipmentDetailSchema>;
